import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { ArrowLeft, Users, Bed, Bath, Wifi, Car, Waves, Phone, Mail } from 'lucide-react';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { getPropertyName, getPropertyDescription, getAmenityTranslation, getPropertyPracticalInfo } from '../translations/propertyTranslations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [property, setProperty] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    fetchProperty();
    fetchCalendar();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendar = async () => {
    try {
      const response = await axios.get(`${API}/calendar/${id}`);
      setCalendar(response.data.events || []);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
  };

  const isDateBooked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendar.some(event => event.date === dateStr && (event.status === 'booked' || event.status === 'blocked'));
  };

  const amenityIcons = {
    'Wi-Fi': <Wifi size={20} />,
    'Wifi': <Wifi size={20} />,
    'Parking': <Car size={20} />,
    'Pool': <Waves size={20} />,
    'Piscine': <Waves size={20} />
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Property not found</h2>
          <Button onClick={() => navigate('/')}>{t('propertyDetail.backToProperties')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header with language selector */}
      <div className="bg-white border-b border-border py-2 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-end gap-4 text-sm">
          <LanguageSelector />
          <div className="flex items-center gap-2">
            <Phone size={16} className="text-accent" />
            <a href="tel:+33611518866" className="hover:text-accent">+33 6 11 51 88 66</a>
          </div>
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-accent" />
            <a href="mailto:guiauchaindiane@orange.fr" className="hover:text-accent">guiauchaindiane@orange.fr</a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        <Button
          data-testid="back-to-home-btn"
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft size={20} className="mr-2" />
          {t('propertyDetail.backToProperties')}
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 gap-4 mb-8">
              {property.images.map((image, index) => (
                <div
                  key={index}
                  className={`overflow-hidden rounded-xl cursor-pointer ${index === 0 ? 'col-span-2' : ''}`}
                  onClick={() => {
                    setPhotoIndex(index);
                    setLightboxOpen(true);
                  }}
                  data-testid={`property-image-${index}`}
                >
                  <img
                    src={image}
                    alt={`${getPropertyName(property.id, language) || property.name} - ${index + 1}`}
                    className="gallery-image w-full h-full object-cover"
                    style={{ aspectRatio: index === 0 ? '16/9' : '4/3' }}
                  />
                </div>
              ))}
            </div>

            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{getPropertyName(property.id, language) || property.name}</h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                {getPropertyDescription(property.id, language) || property.description}
              </p>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4 flex items-center gap-3">
                    <Users className="text-accent" size={24} />
                    <div>
                      <div className="text-sm text-muted-foreground">{t('propertyDetail.guests')}</div>
                      <div className="font-semibold">{property.max_guests}</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-3">
                    <Bed className="text-accent" size={24} />
                    <div>
                      <div className="text-sm text-muted-foreground">{t('propertyDetail.bedrooms')}</div>
                      <div className="font-semibold">{property.bedrooms}</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-3">
                    <Bath className="text-accent" size={24} />
                    <div>
                      <div className="text-sm text-muted-foreground">{t('propertyDetail.bathrooms')}</div>
                      <div className="font-semibold">{property.bathrooms}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h3 className="text-2xl font-semibold mb-4">{t('propertyDetail.amenities')}</h3>
                <div className="flex flex-wrap gap-2">
                  {property.amenities.map((amenity, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="px-4 py-2 text-sm flex items-center gap-2"
                    >
                      {amenityIcons[amenity]}
                      {getAmenityTranslation(amenity, language)}
                    </Badge>
                  ))}
                </div>
              </div>

              {getPropertyPracticalInfo(property.id, language) && (
                <div className="mt-6 p-4 bg-sky-50 dark:bg-sky-950/20 rounded-lg border border-sky-200 dark:border-sky-800">
                  <h3 className="text-xl font-semibold mb-3 text-sky-900 dark:text-sky-100">
                    {language === 'fr' ? 'Informations pratiques' :
                     language === 'es' ? 'Información práctica' :
                     language === 'de' ? 'Praktische Informationen' :
                     language === 'it' ? 'Informazioni pratiche' :
                     language === 'nl' ? 'Praktische informatie' :
                     language === 'ru' ? 'Практическая информация' :
                     language === 'ar' ? 'معلومات عملية' :
                     language === 'zh' ? '实用信息' :
                     language === 'ja' ? '実用情報' :
                     language === 'ko' ? '실용 정보' :
                     language === 'hi' ? 'व्यावहारिक जानकारी' :
                     'Practical Information'}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                    {getPropertyPracticalInfo(property.id, language)}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-4">
            <Card className="sticky top-8">
              <CardContent className="p-6">
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">{t('propertyDetail.availability')}</h4>
                  <div className="border rounded-lg p-3">
                    <Calendar
                      mode="single"
                      className="w-full"
                      modifiers={{
                        booked: (date) => isDateBooked(date)
                      }}
                      modifiersClassNames={{
                        booked: 'calendar-day-booked'
                      }}
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-200"></div>
                      <span>{t('propertyDetail.booked')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-200"></div>
                      <span>{t('propertyDetail.available')}</span>
                    </div>
                  </div>
                </div>

                <Button
                  data-testid="request-booking-btn"
                  className="w-full bg-accent hover:bg-accent/90 h-12 rounded-full font-medium"
                  onClick={() => navigate(`/booking/${property.id}`)}
                >
                  {t('propertyDetail.bookNow')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {lightboxOpen && (
        <Lightbox
          mainSrc={property.images[photoIndex]}
          nextSrc={property.images[(photoIndex + 1) % property.images.length]}
          prevSrc={property.images[(photoIndex + property.images.length - 1) % property.images.length]}
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex((photoIndex + property.images.length - 1) % property.images.length)
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % property.images.length)
          }
        />
      )}
    </div>
  );
}

export default PropertyDetailPage;